import type {
  AnalysisReport,
  AnnotationReport,
  CleanupReport,
  LevelReport,
  ObjectsReport,
  PruneReport,
  StageCostReport,
  VocabReport,
} from "../../api/types";
import type { AnalysisReportKey } from "../../globals/i18n/shared";

/**
 * The thresholds every warning on the report page fires at, gathered here rather
 * than beside the stats they judge: they are tuning knobs read against each other
 * ("is 60% of the time in one stage worse than 50% of the output cap?"), and a
 * knob spread across five components is one nobody can retune with any
 * confidence.
 */

/** A single stage holding more of the run's time than this dominates it. */
const STAGE_TIME_SHARE_WARN = 0.6;
/** No single stage should take longer than this. */
const STAGE_TIME_MS_WARN = 2 * 60 * 1000;
/** Dollars per recording hour, over the whole run, we are willing to spend. */
const COST_PER_HOUR_WARN = 1;
/** Input tokens per call above which the model's output quality may degrade. */
const IN_PER_CALL_WARN = 20_000;
/** Share of the raw events the short-tab-visit pass may drop. */
const SHORT_TAB_DROP_SHARE_WARN = 0.1;
/** Share of the raw events that should survive cleanup. */
const CLEANED_SHARE_WARN = 0.3;
/** Share of the click candidates that should come back annotated. */
const ANNOTATED_SHARE_WARN = 0.8;
/** Annotated clicks per recording hour above which the click rate is suspect. */
const ANNOTATED_PER_HOUR_WARN = 1000;
/** Share of the recording's screenshots the annotator should have read. */
const SCREENSHOTS_USED_SHARE_WARN = 0.6;
/** How far from the click instant its concurrent/post-click frames may sit, ms. */
const CONCURRENT_DELAY_MS_WARN = 100;
const POST_DELAY_MS_WARN = 500;
/** Children per node below which a level barely groups anything. */
const AVG_CHILDREN_WARN = 1.5;
/** Fragmentation above which a level's nodes are scattered across their spans. */
const FRAGMENTATION_WARN = 0.4;
/** Distinct apps per node above which a node is doing too many things at once. */
const AVG_APPS_WARN = 5;
/** Share of a level's time that may be orphaned. */
const ORPHANED_SHARE_WARN = 0.15;
/** Share of the recording L4 must structure. */
const STRUCTURED_COVERAGE_WARN = 0.6;
/** Share of the recording the end-of-pipeline pruning may discard. */
const PRUNED_SHARE_WARN = 0.15;
/** Re-uses per invented name below which the model is coining one-off names. */
const AVG_REUSE_WARN = 1;
/** Clipboard copy→paste pairs per recording hour above which the rate is suspect. */
const CLIPBOARD_PAIRS_PER_HOUR_WARN = 10;
/** Canonical business objects per recording hour above which there are too many. */
const BUSINESS_OBJECTS_PER_HOUR_WARN = 5;

/**
 * The depths whose nodes are expected to span several apps at once. L1 actions
 * carry no app-usage attributes at all, and an L4 instance is long enough that
 * touching many apps is its normal shape — only the two levels between them are
 * judged on it.
 */
const AVG_APPS_DEPTHS = [2, 3];

/** The level whose nodes are the hierarchy's roots — the only one that prunes. */
const ROOT_DEPTH = 4;

/**
 * One flagged figure: which stat it sits on, and the message explaining it.
 *
 * The message is a key and its params rather than a rendered string, so this
 * whole module stays pure and translatable — the component that draws the marker
 * is the one that calls `t`.
 */
export type Warning = {
  /** The stat this hangs off; see the `warn*` id helpers below. */
  id: string;
  /**
   * Absent where the marker says everything there is to say: the figure it sits
   * on is the reading, and the flag beside it is that the reading is out of
   * bounds. Those markers draw bare, with nothing to hover.
   */
  key?: AnalysisReportKey;
  params?: Record<string, string | number>;
};

/**
 * Every warning the report carries, grouped by the section that renders it. A
 * section reads its own list twice: once for the count in its title bar, once per
 * stat for the marker beside it — so the header can never disagree with what
 * opening the section shows.
 */
export type ReportWarnings = {
  costs: Warning[];
  cleanup: Warning[];
  annotation: Warning[];
  levels: Warning[];
  vocab: Warning[];
  objects: Warning[];
};

const EMPTY: ReportWarnings = {
  costs: [],
  cleanup: [],
  annotation: [],
  levels: [],
  vocab: [],
  objects: [],
};

/**
 * The stat ids, as functions rather than string literals spread across the
 * components: an id is a contract between the module that raises a warning and
 * the cell that draws it, and a typo in either half fails silently as a marker
 * that never appears.
 */
/**
 * A stage as a column value. The lift stages are named after the level they
 * produce, so the level alone identifies them; the `lift-to-` prefix is repeated
 * noise down a column that is nothing but stages.
 *
 * Here rather than in the table that draws it because a warning raised in this
 * module names its stage in the same form the cell beside it does.
 */
export const fmtStage = (stage: string) => stage.replace(/^lift-to-l(\d+)$/, "L$1");

export const warnId = {
  stageTime: (stage: string) => `time:${stage}`,
  costPerHour: () => "cost_per_hour",
  inPerCall: (stage: string) => `in:${stage}`,
  toolBudget: (stage: string) => `tool_budget:${stage}`,
  visionTruncated: () => "vision_truncated",
  cleanupPass: (pass: string) => `pass:${pass}`,
  cleanedEvents: () => "cleaned_events",
  annotatedClicks: () => "annotated_clicks",
  annotatedPerHour: () => "annotated_per_hour",
  screenshotsUsed: () => "screenshots_used",
  concurrentDelay: () => "concurrent_delay",
  postDelay: () => "post_delay",
  avgChildren: (depth: number) => `avg_children:${depth}`,
  fragmentation: (depth: number) => `fragmentation:${depth}`,
  avgApps: (depth: number) => `avg_apps:${depth}`,
  orphaned: (depth: number) => `orphaned:${depth}`,
  structured: (depth: number) => `structured:${depth}`,
  pruning: () => "pruning",
  avgReuse: (level: string) => `avg_reuse:${level}`,
  clipboardPairsPerHour: () => "clipboard_pairs_per_hour",
  instanceOverlap: () => "instance_overlap",
  businessObjectsPerHour: () => "business_objects_per_hour",
  failedCall: (row: string) => `failed_call:${row}`,
  truncationSplit: (row: string) => `truncation_split:${row}`,
  spanLimitCuts: (stage: string) => `span_limit_cuts:${stage}`,
};

/** The warnings on one stat, in the order they were raised. A stat can carry more than one. */
export const warningsFor = (warnings: Warning[], id: string) => warnings.filter(w => w.id === id);

/** A fraction as a whole percent: 0.62 → "62%". Shared with the report page. */
export const pct = (fraction: number) => `${(fraction * 100).toFixed(0)}%`;
/** `part` of `whole` as a one-decimal percent: → "12.3%". Shared with the report page. */
export const sharePct = (part: number, whole: number) => `${((part / whole) * 100).toFixed(1)}%`;
const num = (value: number) => value.toFixed(1);

/**
 * A count rated against the recording length. Every per-hour figure on the
 * report page is derived here rather than in the pipeline: both halves are
 * already in the payload (`summary.recordingHours` survives even a run with no
 * report), so a baked-in rate would only be one more field that can disagree
 * with the counts it divides — and one that old stored reports lack.
 *
 * `null` rather than zero when the recording has no length: nothing was
 * measured per hour, and a 0 would read as a run that found nothing.
 */
export const perHour = (value: number, hours: number) => (hours > 0 ? value / hours : null);

/**
 * Canonical business objects per recording hour — the L2 canonicalization's "out"
 * count rated against the recording, which is both a stat the Objects section
 * shows and the figure its warning fires on.
 */
export const businessObjectsPerHour = (objects: ObjectsReport, recordingHours: number) =>
  perHour(objects.canonicalization.distinctOut, recordingHours);

// The selectors below are shared with the report page for the same reason as
// `businessObjectsPerHour`: each is both a figure a cell draws and the figure
// its warning rule judges, and deriving it twice is two counts that can drift.

/**
 * The events that survived cleanup — the funnel's last row. With no passes at
 * all nothing was removed, so the raw count stands.
 */
export const cleanedEventCount = (cleanup: CleanupReport) =>
  cleanup.passes.at(-1)?.eventsAfter ?? cleanup.rawEventCount;

/**
 * The events entering funnel pass `index`: the raw count for the first pass,
 * the previous pass's survivors after.
 */
export const passInputCount = (cleanup: CleanupReport, index: number) =>
  index === 0 ? cleanup.rawEventCount : cleanup.passes[index - 1].eventsAfter;

/**
 * Re-uses per invented name: the node that introduces a name has not re-used
 * it, so a name used exactly once scores 0. `null` where no name was invented —
 * nothing was there to re-use.
 */
export const nameReuse = (invented: number, distinctNames: number) =>
  distinctNames > 0 ? (invented - distinctNames) / distinctNames : null;

/** The recording time a level covered but nothing above adopted. */
export const orphanedDurationS = (level: LevelReport) =>
  Math.max((level.coveredDurationS ?? 0) - (level.structuredDurationS ?? 0), 0);

/** One level's bar total — covered plus uncovered seconds, the denominator its shares are drawn against. */
export const levelTotalS = (level: LevelReport) =>
  (level.coveredDurationS ?? 0) + (level.uncoveredDurationS ?? 0);

const costWarnings = (
  stages: StageCostReport[],
  totalCostUsd: number,
  totalDurationMs: number | null,
  recordingHours: number,
): Warning[] => {
  const warnings: Warning[] = [];

  const costPerHour = perHour(totalCostUsd, recordingHours);
  if (costPerHour !== null && costPerHour > COST_PER_HOUR_WARN) {
    warnings.push({ id: warnId.costPerHour() });
  }

  for (const stage of stages) {
    const durationMs = stage.durationMs;
    if (durationMs != null) {
      // Share and absolute length are separate readings of the same cell: a
      // stage can dominate a fast run without being slow, and be slow in a run
      // it does not dominate. Either one flags the cell.
      const dominates =
        totalDurationMs !== null &&
        totalDurationMs > 0 &&
        durationMs / totalDurationMs > STAGE_TIME_SHARE_WARN;
      if (dominates || durationMs > STAGE_TIME_MS_WARN) {
        warnings.push({ id: warnId.stageTime(stage.stage) });
      }
    }

    // Every per-call figure divides by the billed calls, so a stage that made
    // none (an empty frontier, or a row from before the counters existed) has
    // nothing to judge rather than a zero to flag.
    const apiCalls = stage.apiCalls ?? 0;
    if (apiCalls > 0) {
      const inPerCall = stage.inputTokens / apiCalls;
      if (inPerCall > IN_PER_CALL_WARN) {
        warnings.push({
          id: warnId.inPerCall(stage.stage),
          key: "warning_in_per_api_call",
          params: {
            tokens: Math.round(inPerCall).toLocaleString(),
            limit: IN_PER_CALL_WARN.toLocaleString(),
          },
        });
      }
    }

    // Any call that ran out of rounds is worth flagging, so there is no
    // threshold to clear: the model was cut off mid-investigation and answered
    // anyway, which is a quality risk the token counts say nothing about.
    const exhausted = stage.toolBudgetExhausted ?? 0;
    if (exhausted > 0) {
      warnings.push({
        id: warnId.toolBudget(stage.stage),
        key: "warning_tool_budget_exhausted",
        params: { count: exhausted, stage: fmtStage(stage.stage), max: stage.toolRoundCap ?? 0 },
      });
    }
  }

  return warnings;
};

const cleanupWarnings = (cleanup: CleanupReport | null): Warning[] => {
  if (!cleanup) return [];
  const raw = cleanup.rawEventCount;
  if (raw <= 0) return [];
  const warnings: Warning[] = [];

  const shortTabIndex = cleanup.passes.findIndex(p => p.pass === "drop-short-tab-visits");
  if (shortTabIndex >= 0) {
    const before = passInputCount(cleanup, shortTabIndex);
    const dropped = before - cleanup.passes[shortTabIndex].eventsAfter;
    if (dropped / raw > SHORT_TAB_DROP_SHARE_WARN) {
      warnings.push({
        id: warnId.cleanupPass("drop-short-tab-visits"),
        key: "warning_short_tab_drop",
        params: {
          share: sharePct(dropped, raw),
          limit: pct(SHORT_TAB_DROP_SHARE_WARN),
        },
      });
    }
  }

  // With no passes at all the cleaned count is the raw count — share 1, so
  // nothing was removed to warn about.
  const cleaned = cleanedEventCount(cleanup);
  if (cleaned / raw < CLEANED_SHARE_WARN) {
    warnings.push({
      id: warnId.cleanedEvents(),
      key: "warning_cleaned_events",
      params: { share: sharePct(cleaned, raw), limit: pct(CLEANED_SHARE_WARN) },
    });
  }

  return warnings;
};

const annotationWarnings = (
  annotation: AnnotationReport | null,
  recordingHours: number,
): Warning[] => {
  if (!annotation) return [];
  const warnings: Warning[] = [];

  // No candidate at all is a recording with no clicks, not an annotator that
  // failed: there is no share to fall short of.
  if (annotation.clicksTotal > 0) {
    const annotatedShare = annotation.clicksAnnotated / annotation.clicksTotal;
    if (annotatedShare < ANNOTATED_SHARE_WARN) {
      warnings.push({
        id: warnId.annotatedClicks(),
        key: "warning_annotated_share",
        params: {
          share: pct(annotatedShare),
          limit: pct(ANNOTATED_SHARE_WARN),
        },
      });
    }
  }

  // A truncated vision response was cut off at the gateway's output cap: the
  // click died to configuration rather than model quality, and any count of
  // them warrants the flag — the fix is raising the alias's limit, not re-running.
  const truncated = annotation.visionFailures.filter(f => f.kind === "truncated").length;
  if (truncated > 0) {
    warnings.push({
      id: warnId.visionTruncated(),
      key: "warning_n_vision_truncated",
      params: { count: truncated },
    });
  }

  const annotatedPerHour = perHour(annotation.clicksAnnotated, recordingHours);
  if (annotatedPerHour !== null && annotatedPerHour > ANNOTATED_PER_HOUR_WARN) {
    warnings.push({
      id: warnId.annotatedPerHour(),
      key: "warning_annotated_per_hour",
      params: {
        rate: num(annotatedPerHour),
        limit: ANNOTATED_PER_HOUR_WARN.toLocaleString(),
      },
    });
  }

  if (annotation.screenshotsTotal > 0) {
    const usedShare = annotation.screenshotsUsed / annotation.screenshotsTotal;
    if (usedShare < SCREENSHOTS_USED_SHARE_WARN) {
      warnings.push({
        id: warnId.screenshotsUsed(),
        key: "warning_screenshots_used",
        params: {
          share: pct(usedShare),
          limit: pct(SCREENSHOTS_USED_SHARE_WARN),
        },
      });
    }
  }

  // The delays are magnitudes as the pipeline records them, so they compare
  // against the bound directly. `null` is a run that measured no sample.
  const concurrent = annotation.concurrentDelayMeanMs;
  if (concurrent != null && concurrent > CONCURRENT_DELAY_MS_WARN) {
    warnings.push({
      id: warnId.concurrentDelay(),
      key: "warning_concurrent_delay",
      params: {
        delay: `${Math.round(concurrent)} ms`,
        limit: `${CONCURRENT_DELAY_MS_WARN} ms`,
      },
    });
  }
  const post = annotation.postDelayMeanMs;
  if (post != null && post > POST_DELAY_MS_WARN) {
    warnings.push({
      id: warnId.postDelay(),
      key: "warning_post_delay",
      params: {
        delay: `${Math.round(post)} ms`,
        limit: `${POST_DELAY_MS_WARN} ms`,
      },
    });
  }

  return warnings;
};

const levelWarnings = (
  levels: LevelReport[],
  pruning: PruneReport | null | undefined,
): Warning[] => {
  const warnings: Warning[] = [];

  for (const level of levels) {
    // An empty level's figures are drawn as em dashes rather than zeroes — there
    // is nothing there to be badly shaped.
    if (level.nodeCount === 0) continue;

    if (level.avgChildrenPerParent < AVG_CHILDREN_WARN) {
      warnings.push({
        id: warnId.avgChildren(level.depth),
        key: "warning_avg_children",
        params: {
          depth: level.depth,
          value: num(level.avgChildrenPerParent),
          limit: num(AVG_CHILDREN_WARN),
        },
      });
    }

    const fragmentation = level.fragmentationScore ?? 0;
    if (fragmentation > FRAGMENTATION_WARN) {
      warnings.push({
        id: warnId.fragmentation(level.depth),
        key: "warning_fragmentation",
        params: {
          depth: level.depth,
          value: fragmentation.toFixed(2),
          limit: FRAGMENTATION_WARN.toFixed(2),
        },
      });
    }

    if (
      AVG_APPS_DEPTHS.includes(level.depth) &&
      level.avgAppsPerNode != null &&
      level.avgAppsPerNode > AVG_APPS_WARN
    ) {
      warnings.push({
        id: warnId.avgApps(level.depth),
        key: "warning_avg_apps",
        params: {
          depth: level.depth,
          value: num(level.avgAppsPerNode),
          limit: String(AVG_APPS_WARN),
        },
      });
    }

    // Rated against the level's own bar total, so the warning and the percentage
    // drawn under the bar are the same division rather than two that round apart.
    const total = levelTotalS(level);
    if (total <= 0) continue;
    const structured = level.structuredDurationS ?? 0;

    const orphanedShare = orphanedDurationS(level) / total;
    if (orphanedShare > ORPHANED_SHARE_WARN) {
      warnings.push({
        id: warnId.orphaned(level.depth),
        key: "warning_orphaned",
        params: {
          depth: level.depth,
          share: pct(orphanedShare),
          limit: pct(ORPHANED_SHARE_WARN),
        },
      });
    }

    // Only at the roots: a lower level's structured share is the next level's
    // input, not the pipeline's output, so falling short there is what the
    // orphan warning above already says.
    if (level.depth === ROOT_DEPTH && structured / total < STRUCTURED_COVERAGE_WARN) {
      warnings.push({
        id: warnId.structured(level.depth),
        key: "warning_structured_coverage",
        params: {
          share: pct(structured / total),
          limit: pct(STRUCTURED_COVERAGE_WARN),
        },
      });
    }
  }

  const rootTotal = levels
    .filter(l => l.depth === ROOT_DEPTH)
    .map(levelTotalS)
    .find(t => t > 0);
  if (pruning && rootTotal) {
    const prunedS = pruning.tooShortDurationS + pruning.singleChildDurationS;
    if (prunedS / rootTotal > PRUNED_SHARE_WARN) {
      warnings.push({
        id: warnId.pruning(),
        key: "warning_pruning",
        params: {
          share: pct(prunedS / rootTotal),
          limit: pct(PRUNED_SHARE_WARN),
        },
      });
    }
  }

  return warnings;
};

/**
 * One level's re-use figure, judged only where there is one: a level that
 * invented no name re-used nothing, and its row is an em dash rather than a zero.
 */
const reuseWarning = (level: string, invented: number, inventedNameCount: number): Warning[] => {
  const reuse = nameReuse(invented, inventedNameCount);
  return reuse !== null && reuse < AVG_REUSE_WARN
    ? [
        {
          id: warnId.avgReuse(level),
          key: "warning_avg_reuse",
          params: { level, value: num(reuse), limit: num(AVG_REUSE_WARN) },
        },
      ]
    : [];
};

const vocabWarnings = (vocab: VocabReport): Warning[] => [
  ...reuseWarning("L3", vocab.stepNodesInvented, vocab.inventedStepNames.length),
  ...reuseWarning("L4", vocab.processNodesInvented, vocab.inventedProcessNames.length),
];

const objectWarnings = (objects: ObjectsReport, recordingHours: number): Warning[] => {
  const warnings: Warning[] = [];

  const pairsPerHour = perHour(objects.clipboard.pastesPaired, recordingHours);
  if (pairsPerHour !== null && pairsPerHour > CLIPBOARD_PAIRS_PER_HOUR_WARN) {
    warnings.push({
      id: warnId.clipboardPairsPerHour(),
      key: "warning_clipboard_pairs_per_hour",
      params: {
        rate: num(pairsPerHour),
        limit: String(CLIPBOARD_PAIRS_PER_HOUR_WARN),
      },
    });
  }

  // A stored blob from before the overlap was computed shares nothing *that we
  // know of*, which is not the same as instances that shared nothing — and is
  // not something to warn about either way.
  const overlap = objects.instanceOverlap;
  if (overlap && overlap.length > 0) {
    warnings.push({
      id: warnId.instanceOverlap(),
      key: "warning_instance_overlap",
      params: { count: overlap.length },
    });
  }

  const objectsPerHour = businessObjectsPerHour(objects, recordingHours);
  if (objectsPerHour !== null && objectsPerHour > BUSINESS_OBJECTS_PER_HOUR_WARN) {
    warnings.push({
      id: warnId.businessObjectsPerHour(),
      key: "warning_business_objects_per_hour",
      params: {
        rate: num(objectsPerHour),
        limit: String(BUSINESS_OBJECTS_PER_HOUR_WARN),
      },
    });
  }

  return warnings;
};

/**
 * Every warning the report carries, raised in one pass over it.
 *
 * A report with no run-generated half still has a vocabulary section — it is
 * derived from the stored analysis rather than the run — so that one section is
 * judged either way and the rest come back empty.
 */
export const computeReportWarnings = (
  report: AnalysisReport,
  vocab: VocabReport,
  recordingHours: number,
): ReportWarnings => {
  if (report.version !== "V1") return { ...EMPTY, vocab: vocabWarnings(vocab) };
  return {
    costs: costWarnings(report.stages, report.totalCostUsd, report.totalDurationMs, recordingHours),
    cleanup: cleanupWarnings(report.cleanup),
    annotation: annotationWarnings(report.annotation, recordingHours),
    levels: levelWarnings(report.levels, report.pruning),
    vocab: vocabWarnings(vocab),
    objects: objectWarnings(report.objects, recordingHours),
  };
};
